import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Box, CircularProgress, Tooltip, Typography} from "@mui/material";
import EnterpriseObject from "../../eoec/eo/EnterpriseObject";
import enterpriseObjectClassFactory from "../../eoec/EnterpriseObjectClassFactory";
import {prepareBuffer} from "../../util/Buffer";
import useAppContext from "../../util/AppContext";
import EventBus from "../../util/EventBus";
import {ResizeGripper, useResizablePanel} from "./ResizablePanel";
import {statusColor} from "../../util/statusPill";
import {Blue3, Grey5, Grey6, Grey17} from "../../theme/styles";

/**
 * The list you came from, kept beside the record you opened.
 *
 * WHY IT EXISTS. Opening a record used to mean leaving the list entirely: the page unmounts, its
 * enterprise component goes with it, and moving to the next record is Back, find your place, click.
 * Ataccama keeps its table visible and opens detail as a side panel; this is the same idea with the
 * halves swapped, because our detail pages are routed and full of tabs -- turning them into panels
 * would be a rebuild, while a narrow index beside them is additive.
 *
 * FILTER FIDELITY IS THE WHOLE PROBLEM. If somebody filters Rules to twelve Draft records, clicks
 * the third, and this rail shows all twenty-nine, the rail is lying about the list they came from --
 * which is worse than no rail, because it is confidently wrong. The list persists its status
 * selection in sessionStorage precisely so it survives the detail round trip, and this reads the
 * same key. Domain and recency filters are NOT persisted by the list either, so a rail that ignores
 * them matches exactly what the list shows when you navigate back to it.
 *
 * FLAT, EVEN WHEN THE LIST IS GROUPED. This is an index, not a second grid: at this width a
 * hierarchy would spend its space on indentation rather than names, and the detail page already
 * says where the record sits.
 *
 * THE OPEN RECORD IS ALWAYS IN IT. Arriving by bookmark or a shared link, the record may not match
 * the saved filters at all -- so it is pinned at the top and marked, rather than leaving a rail
 * that does not contain the thing you are looking at.
 *
 * IT OBEYS THE PERSONA. The active visibility is what decides which records a user may see at
 * all, and PlatformTable puts it on the component before every query it makes. This builds its own
 * component and so must do the same -- otherwise the rail beside a list shows records the list
 * itself does not, which reads as the product leaking rather than as a component forgetting a
 * header. It re-queries when the persona changes, for the same reason the lists do.
 *
 * IT GROWS BY SCROLLING, NOT BY PAGING. A rail with prev/next is a rail you have to operate to
 * reach the next record, which is the trip back to the list this exists to avoid. Reaching the
 * bottom fetches the next page and appends it.
 */

type Props = {
    /** The enterprise component to list, e.g. "RuleRevision". */
    componentName: string;
    /**
     * The IDOC to ask for. OPTIONAL, and usually better left out: a component that chooses its own
     * -- ObjectRevision names it after the classification it is scoped to -- must be allowed to,
     * because the envelope's -idoc and its payload node have to agree. Forcing
     * "v1.ObjectRevision-001" onto a component whose node is "DocumentRevision" produced an envelope
     * the server could find no query groups for, and it failed with 'NoneType' has no attribute
     * 'items'. Set it only for a component that does NOT pick one.
     */
    idoc?: string;
    /** The record currently open, so it can be highlighted and, if need be, pinned. */
    currentUSID?: string | null;
    /** Where clicking a row goes. */
    routeFor: (usid: string) => string;
    navigate: (to: string) => void;
    /** sessionStorage key holding the list's status selection, if it keeps one. */
    statusStorageKey?: string;
    /** What the list shows when that key has never been written. MUST match the list's own default. */
    defaultStatuses?: string[];
    /**
     * Scope the component before it is asked anything, for a list that is narrower than its
     * enterprise component. Every Foundation classification -- Document, Contract, PurchaseOrder --
     * is ObjectRevision rows, so a rail beside a Document must say so or it lists the whole estate.
     * Called after newQuery() and before the filters, which is the only order that survives a reset.
     */
    configure?: (ec: any) => void;
    /** Heading above the rail. */
    title: string;
    /**
     * Facts about each row that its own document does not carry, asked ONCE per page of rows:
     * a pill beside the identifier (a project's customer -- Ed, 2026-10-29: "the number, object
     * id, the customer the project is associated to in a pill, the name") and the counts under
     * its children. Keyed by the row's logical ObjectUSID; a row the answer does not name draws
     * neither. One ask rather than one per row, because the facts live on other tables and
     * twenty-five reads for one column is how a rail gets slow.
     */
    facts?: (rows: {usid: string; objectUSID: string | null}[]) =>
        Promise<Record<string, {pill?: string | null; counts?: Record<string, number>}>>;
    /**
     * What EVERY listed record holds, drawn as a tree under its row: one child per kind, its
     * count on the right, each opening a pane of the record page. Ed, 2026-10-29: "Each project
     * in the list should show Products, Contracts, Quote Requests, Quote Responses, Build Plans"
     * -- every row, not only the open one, so the rail reads as the folders and their contents.
     */
    children?: {key: string; label: string; icon?: React.ElementType}[];
    /** The open record's live counts, which win over `facts` for that row so a record filed a
     *  moment ago is counted without a re-ask. */
    currentCounts?: Record<string, number>;
    /** The child whose pane is open on the CURRENT record, and what choosing one does: on the open
     *  record, open that pane; on another, go there and open it. Clicking an open record's own row
     *  opens "Detail". */
    childSelected?: string;
    onChildSelect?: (entry: {usid: string; isCurrent: boolean}, key: string) => void;
};

const DEFAULT_WIDTH = 204;
const MIN_WIDTH = 150;
const MAX_WIDTH = 420;

// Dragged narrower than this, the rail closes rather than becoming a column too thin to read. Below
// MIN_WIDTH so the two do not fight: between them the rail snaps open to MIN, or shut.
const COLLAPSE_AT = 120;


// A PAGE IS A TOP-UP, NOT A SCREENFUL. No constant can know how tall the window is -- 25 leaves a
// tall screen half empty and a short one scrolling immediately -- so the rail keeps asking for
// pages until its own content overflows, then goes back to loading on scroll. See fillViewport.
// 100 was the first guess and it was wrong twice over: it fetched four screens nobody scrolled to,
// and against Peak's 29 rules every one arrived on page one, so scroll-loading never fired at all.
const PAGE_SIZE = 25;

// How close to the bottom counts as reaching it. Generous, so the next page is arriving before the
// scrollbar stops rather than after it.
const LOAD_MORE_MARGIN = 120;

export default function RecordRail({componentName, idoc, currentUSID, routeFor, navigate,
                                    statusStorageKey, defaultStatuses, configure, title,
                                    facts, children, currentCounts, childSelected, onChildSelect}: Props) {
    const appContext: any = useAppContext();
    const sessionContext = appContext?.sessionContext;

    // Bumped when the persona changes, to re-run the query effect below.
    const [visibilityTick, setVisibilityTick] = useState(0);

    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [total, setTotal] = useState<number | null>(null);

    const {width, collapsed, setCollapsed, panelRef, onGripperDown} = useResizablePanel({
        storageKey: "RecordRail", defaultWidth: DEFAULT_WIDTH,
        minWidth: MIN_WIDTH, maxWidth: MAX_WIDTH, collapseAt: COLLAPSE_AT,
    });

    // The page last ASKED for. A ref rather than state because the load-completed listener is
    // registered once and closes over whatever it captured -- state would be stale inside it.
    const page = useRef(1);
    const ecRef = useRef<any>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);

    const [eo] = useState<EnterpriseObject>(
        () => enterpriseObjectClassFactory(componentName) as EnterpriseObject);

    const statuses = useMemo(() => {
        // THE DEFAULT APPLIES WHETHER OR NOT A KEY EXISTS. This used to return early when no
        // statusStorageKey was passed, which silently threw away defaultStatuses -- so a host that
        // supplied a default but no key (t1 has no persisted status selection to read) got an
        // UNFILTERED rail: deprecated and superseded records listed beside a list showing neither.
        // The key is an override of the default, not a precondition for it.
        if (!statusStorageKey) return defaultStatuses ?? null;
        try {
            const saved = JSON.parse(sessionStorage.getItem(statusStorageKey) ?? "null");
            // THE ABSENT KEY IS THE COMMON CASE, not the edge one. A list writes it when somebody
            // TOUCHES a filter, so on a fresh tab -- or a link opened straight to a record -- it has
            // never been written, and falling back to "no filter" is not the same question the list
            // asked itself. It showed 24 rules on its defaults while the rail, unfiltered, showed 19:
            // two answers on one screen, which is the exact failure this rail exists to avoid.
            return Array.isArray(saved) ? saved : (defaultStatuses ?? null);
        } catch {
            // A corrupt key is not worth failing over -- an unfiltered rail is still useful, and a
            // blank page beside a working record is not.
            return null;
        }
    }, [statusStorageKey, defaultStatuses]);

    useEffect(() => {
        const ec: any = eo?.getEnterpriseComponent(componentName);
        if (!ec) return;
        ecRef.current = ec;

        if (idoc) {
            ec.setIDOCRequest(idoc);
            ec.setIDOCResponse(idoc);
        }

        // newQuery() BEFORE the filters, which is the house rule and not ceremony: a component
        // carries whatever was asked of it last, and setting a filter onto un-reset state asks a
        // question nobody wrote.
        //
        // NO ARGUMENT. The frontend's newQuery takes none -- unlike the Python new_query('Default'),
        // which selects a view. Passing one is ignored silently and reads as though a view were
        // being chosen.
        ec.newQuery();

        // The host's own scoping, before anything else is asked of the component.
        if (configure) configure(ec);

        // THE PERSONA, BEFORE THE QUERY. RuleRevisionEnterpriseComponent reads these straight off
        // the component into the visibility-mode / visibility-value headers, and the server scopes
        // the result by them. Unset, the rail asks an unscoped question and can list records the
        // list it sits beside will not show.
        const visibility = sessionContext?.getActiveVisibility?.();
        if (ec.setVisibilityMode) ec.setVisibilityMode(visibility?.Mode);
        if (ec.setVisibilityValue) ec.setVisibilityValue(visibility?.Value);

        if (statuses && statuses.length > 0 && ec.setStatusesFilter) ec.setStatusesFilter(statuses);

        // The listener is invoked as a plain function -- notifyLoadCompletedListeners calls
        // listener(this) -- so a callback is what it wants, not an object with a method.
        ec.addLoadCompletedListener(() => {
            const loaded = prepareBuffer(ec) ?? [];
            setRows(previous => {
                // APPENDED, NOT REPLACED, beyond the first page. The component buffers one page at
                // a time and moves its page pointer, so each load hands back only what it just
                // fetched; the accumulated list is this component's to keep.
                if (page.current <= 1) return loaded;
                const seen = new Set(previous.map((one: any) => String(one?.get?.("USID"))));
                return [...previous,
                        ...loaded.filter((one: any) => !seen.has(String(one?.get?.("USID"))))];
            });
            setTotal(typeof ec.getTotalRowcount === "function" ? ec.getTotalRowcount() : null);
            setLoading(false);
            setLoadingMore(false);
        });

        // BACK TO PAGE ONE whenever this re-runs. Switching persona changes which records exist for
        // this user, so the pages already accumulated describe somebody else's list.
        page.current = 1;
        setRows([]);
        setLoading(true);

        ec.executeQuery(1, PAGE_SIZE);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eo, componentName, idoc, statuses, visibilityTick, configure]);

    // Re-query when the persona changes, the same signal the lists listen for.
    useEffect(() => {
        const onChangeVisibility = () => {
            ecRef.current?.invalidate?.();
            setVisibilityTick(t => t + 1);
        };
        EventBus.visibility.addListener("change-visibility", onChangeVisibility);
        return () => { EventBus.visibility.removeListener("change-visibility", onChangeVisibility); };
    }, []);

    const entries = useMemo(() => {
        // THE IDENTIFIER AND THE STATE COME WITH THE NAME. A rail of names alone cannot be matched
        // against the list it came from -- two documents called "Cleanroom Gowning Work Instruction
        // 11" are told apart by SOP number and by nothing else -- and a reader scanning for the
        // draft they were editing should not have to open each one to find out which is which.
        // Optional chaining throughout: a component without these attributes still lists names.
        const out = rows
            .map((row: any) => ({
                usid: row?.get?.("USID"),
                objectUSID: row?.get?.("ObjectUSID") ?? null,
                name: row?.get?.("Name"),
                objectId: row?.get?.("ObjectID") ?? null,
                status: row?.get?.("Status") ?? null,
                pinned: false,
            }))
            .filter(one => one.usid);

        if (currentUSID && !out.some(one => String(one.usid) === String(currentUSID))) {
            // Pinned, and marked, because a rail that does not contain the open record invites the
            // reader to conclude it is showing something else entirely.
            return [{usid: currentUSID, objectUSID: null, name: null, objectId: null, status: null, pinned: true}, ...out];
        }
        return out;
    }, [rows, currentUSID]);

    const loaded = entries.filter(one => !one.pinned).length;

    // THE FACTS, ONE ASK PER CHANGE OF ROWS. Keyed by logical object, held separately from the
    // entries so a slow answer never blanks a rail that has already drawn its names.
    const [factsByObject, setFactsByObject] = useState<Record<string, {pill?: string | null; counts?: Record<string, number>}>>({});
    useEffect(() => {
        if (!facts) return;
        const asked = entries.filter(one => one.objectUSID).map(one => ({usid: String(one.usid), objectUSID: one.objectUSID}));
        if (asked.length === 0) return;
        let live = true;
        facts(asked).then(answer => { if (live) setFactsByObject(answer ?? {}); })
                    .catch(() => { if (live) setFactsByObject({}); });
        return () => { live = false; };
        // Keyed on the objects listed, not the array: entries is rebuilt per render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entries.map(one => one.objectUSID).join("|"), facts]);
    const more = total !== null && loaded < total;

    const loadNextPage = useCallback(() => {
        setLoadingMore(true);
        page.current += 1;
        ecRef.current?.executeQuery(page.current, PAGE_SIZE);
    }, []);

    const onScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        if (!more || loadingMore || loading) return;
        const element = event.currentTarget;
        if (element.scrollHeight - element.scrollTop - element.clientHeight > LOAD_MORE_MARGIN) return;

        loadNextPage();
    }, [more, loadingMore, loading, loadNextPage]);

    // FILL THE WINDOW BEFORE WAITING FOR A SCROLL. Until the list is tall enough to scroll there is
    // no scroll event to load on, so a page smaller than the screen leaves the rail short with
    // nothing the reader can do about it -- and no indication that more records exist. Whenever the
    // content still fits, the next page is fetched; a tall window simply takes a few more rounds.
    // This is also what makes PAGE_SIZE a tuning choice rather than a promise about what is visible.
    useEffect(() => {
        if (!more || loading || loadingMore) return;
        const element = scrollRef.current;
        if (!element) return;
        if (element.scrollHeight > element.clientHeight + LOAD_MORE_MARGIN) return;

        loadNextPage();
    }, [entries, more, loading, loadingMore, width, collapsed, loadNextPage]);

    return (
        <Box ref={panelRef} sx={{display: "flex", flexDirection: "row", flexShrink: 0, minHeight: 0}}>
            {!collapsed && (
                <Box ref={scrollRef} onScroll={onScroll}
                     sx={{width, flexShrink: 0, overflowY: "auto",
                          display: {xs: "none", md: "block"}, py: 2.5, px: 1.5}}>
                    <Typography sx={{fontSize: 11, fontWeight: 700, color: Grey6, letterSpacing: "0.5px",
                                     textTransform: "uppercase", mb: 1}}>
                        {title}
                        {statuses && statuses.length > 0 ? (
                            // Says WHY the rail is shorter than the whole table. Without it a
                            // filtered rail looks like one that failed to load the rest.
                            <span style={{fontWeight: 400, textTransform: "none"}}> · filtered</span>
                        ) : null}
                    </Typography>

                    {loading ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 3}}>
                            <CircularProgress size={16}/>
                        </Box>
                    ) : entries.length === 0 ? (
                        <Typography sx={{fontSize: 12, color: Grey6}}>Nothing to list.</Typography>
                    ) : entries.map((entry: any, index: number) => {
                        const isCurrent = String(entry.usid) === String(currentUSID);
                        return (
                            <React.Fragment key={entry.usid}>
                            <div onClick={() => {
                                     if (!isCurrent) navigate(routeFor(entry.usid));
                                     else if (onChildSelect) onChildSelect({usid: String(entry.usid), isCurrent}, "Detail");
                                 }}
                                 title={[entry.objectId, entry.name].filter(Boolean).join(" — ") || "This record is outside the current filter"}
                                 style={{
                                     display: "flex", gap: 8, alignItems: "flex-start", padding: "5px 6px",
                                     marginBottom: 1, borderRadius: 4,
                                     cursor: isCurrent && !onChildSelect ? "default" : "pointer",
                                     backgroundColor: isCurrent ? "rgba(20,152,216,0.10)" : "transparent",
                                 }}>
                                {/* The record pointer, which is what the list calls its rows and
                                    what makes a position here correspond to a position there. */}
                                <Typography sx={{fontSize: 11, color: Grey5, minWidth: 18, textAlign: "right",
                                                 fontVariantNumeric: "tabular-nums", flexShrink: 0}}>
                                    {entry.pinned ? "—" : index + 1}
                                </Typography>
                                <div style={{display: "flex", flexDirection: "column", gap: 1, minWidth: 0, flex: 1}}>
                                    {(entry.objectId || entry.status) && (
                                        <div style={{display: "flex", alignItems: "center", gap: 6, minWidth: 0}}>
                                            <Typography sx={{fontSize: 11, color: Grey5, letterSpacing: "0.2px",
                                                             fontVariantNumeric: "tabular-nums",
                                                             overflow: "hidden", textOverflow: "ellipsis",
                                                             whiteSpace: "nowrap"}}>
                                                {entry.objectId}
                                            </Typography>
                                            {/* THE ROW'S OWN PILL -- a project's customer -- on
                                                the identifier line, where it reads as a fact
                                                about the record rather than part of its name.
                                                Ellipsised, with the whole text in its title. */}
                                            {entry.objectUSID && factsByObject[entry.objectUSID]?.pill && (
                                                <span title={factsByObject[entry.objectUSID].pill ?? undefined}
                                                      style={{fontSize: 10, color: Grey6, border: `1px solid ${Grey5}`,
                                                              borderRadius: 9, padding: "0 6px", lineHeight: "15px",
                                                              maxWidth: 96, overflow: "hidden", textOverflow: "ellipsis",
                                                              whiteSpace: "nowrap", flexShrink: 1, minWidth: 0}}>
                                                    {factsByObject[entry.objectUSID].pill}
                                                </span>
                                            )}
                                            {/* A DOT, NOT A PILL. At this width a chip would take
                                                half the row to say what a coloured dot says, and the
                                                dot reads down a column of them -- which is how the
                                                question is actually asked: "which of these are still
                                                drafts". The colour comes from the SAME palette the
                                                pills use, and the title names it for anyone who
                                                cannot tell the colours apart. */}
                                            {entry.status && (
                                                <Tooltip title={entry.status} placement="right">
                                                    <span style={{
                                                        width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                                                        backgroundColor: statusColor(entry.status),
                                                        marginLeft: "auto",
                                                    }}/>
                                                </Tooltip>
                                            )}
                                        </div>
                                    )}
                                    <Typography sx={{
                                        fontSize: 13.5,
                                        color: isCurrent ? Blue3 : Grey17,
                                        fontWeight: isCurrent ? 600 : 400,
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    }}>
                                        {entry.name ?? "This record"}
                                    </Typography>
                                </div>
                            </div>
                            {/* EVERY RECORD'S CONTENTS, as a tree under its row. Each child is a
                                pane of the record page: picking one here is picking it there, on
                                this record or after going to that one. The open record's counts
                                are the live ones. */}
                            {children && children.length > 0 && entry.objectUSID && (() => {
                                const counts = isCurrent && currentCounts ? currentCounts
                                             : factsByObject[entry.objectUSID]?.counts;
                                return (
                                <div data-testid="rail-children" style={{marginBottom: 4}}>
                                    {children.map(child => {
                                        const Icon = child.icon;
                                        const isSelected = isCurrent && child.key === childSelected;
                                        return (
                                            <div key={child.key} role="button" data-pane={child.key}
                                                 onClick={() => onChildSelect?.({usid: String(entry.usid), isCurrent}, child.key)}
                                                 style={{display: "flex", alignItems: "center", gap: 7,
                                                         padding: "4px 6px 4px 32px", marginBottom: 1, borderRadius: 4,
                                                         cursor: "pointer", minWidth: 0,
                                                         backgroundColor: isSelected ? "rgba(20,152,216,0.10)" : "transparent"}}>
                                                {Icon && <Icon sx={{width: 16, height: 16, color: Blue3, flexShrink: 0}}/>}
                                                <Typography sx={{fontSize: 12.5, color: isSelected ? Blue3 : Grey17,
                                                                 fontWeight: isSelected ? 600 : 400, flex: 1, minWidth: 0,
                                                                 overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                                    {child.label}
                                                </Typography>
                                                <Typography sx={{fontSize: 11, color: Grey5, flexShrink: 0,
                                                                 fontVariantNumeric: "tabular-nums"}}>
                                                    {counts ? (counts[child.key] ?? 0) : ""}
                                                </Typography>
                                            </div>
                                        );
                                    })}
                                </div>
                                );
                            })()}
                            </React.Fragment>
                        );
                    })}

                    {loadingMore ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 1.5}}>
                            <CircularProgress size={14}/>
                        </Box>
                    ) : more ? (
                        // NO SILENT TRUNCATION while there is more to come. Scrolling fetches it,
                        // but somebody who has not scrolled yet should know the list continues.
                        <Typography sx={{fontSize: 11, color: Grey5, pt: 1.5, textAlign: "center"}}>
                            {loaded} of {total} — scroll for more
                        </Typography>
                    ) : null}
                </Box>
            )}

            <ResizeGripper collapsed={collapsed} onMouseDown={onGripperDown}
                           setCollapsed={setCollapsed} openLabel={`Open ${title}`}/>
        </Box>
    );
}
